import React from 'react';
import {Button, ButtonGroup, Menu, MenuItem, Icon, Intent, Position} from '@blueprintjs/core';
import {Popover2} from '@blueprintjs/popover2';
import {IconNames} from '@blueprintjs/icons';

import {EXECUTION_STATUS} from '../../constants';
import {formatDate} from '../../../../utils/date';
import {useGetResult} from '../../hooks';

import './SelectResult.scss';

interface SelectResultProps {
  uid: string;
  selected?: string;
  onSelect?: (value: string) => void;
  disabled?: boolean;
}

const LIMIT_RESULTS = 10;
const RESULTS_IN_TABS = 3;

const _resultsInTab = (_: any, index: number) => index < RESULTS_IN_TABS;
const _resultsInMenu = (_: any, index: number) => index >= RESULTS_IN_TABS;

const _toResult = (result: any) => {
  /*
   * We're interested only in these values
   */
  return {
    key: result.key,
    status: result.status,
    original_status: result.original_status,
    start_time: result.start_time,
  }
}

const _findResult = (results: any [], key: string) => {
  if (!results?.length) {
    return null;
  } else if (!key) {
    return results[0].key;
  }
  return results.find((r: any) => r.key === key) || null;
};

const _getStartTime = (result: any): string => {
  if (result) {
    return formatDate(result.start_time);
  }
  return '';
};

const _getIntent = (result: any) => {
  const {status, original_status} = result;
  const _status = original_status || status;

  let intent: Intent = Intent.NONE;
  if (_status === EXECUTION_STATUS.FAILED) {
    intent = Intent.DANGER;
  } else if (_status === EXECUTION_STATUS.CANCELLED) {
    intent = Intent.WARNING;
  }
  return intent;
};

interface MenuContentProps {
  results: any;
  selected: string;
  onSelect: (value: string) => void;
}

const MenuContent = React.memo(({
  results,
  selected,
  onSelect,
}: MenuContentProps) => {
  return (
    <Menu>
      {
        results
          .filter((_: any, index: number) => index < LIMIT_RESULTS)
          .map((result: any) => (
            <MenuItem
              key={result.key}
              text={_getStartTime(result)}
              labelElement={selected === result.key && <Icon icon={IconNames.TICK}/>}
              onClick={() => onSelect(result.key)}
              intent={_getIntent(result)}
              icon={IconNames.CALENDAR}
            />
        ))
      }
    </Menu>
  );
});

export const SelectResult = ({
  uid,
  selected = '',
  onSelect,
  disabled = false,
}: SelectResultProps) => {
  /*
   * List last results in order to retrieve them
   */
  let {results} = useGetResult(uid, null, false);
  results = results?.map(_toResult);
  const totalResults = results?.length || 0;

  const _onSelect = (value: any) => {
    onSelect && onSelect(value);
  };

  return (
    <div className="select-last-results">
      <ButtonGroup
        onChange={_onSelect}
        id={selected}
        className="select-last-results__tabs"
      >
        {
          results.filter(_resultsInTab).map((r: any) => (
            <Button
              text={formatDate(r.start_time)}
              intent={_getIntent(r)}
              minimal={true}
              small={true}
              icon={IconNames.CALENDAR}
              onClick={() => _onSelect(r.key)}
              active={r.key === selected}
            />
          ))
        }
        {
          totalResults > RESULTS_IN_TABS && (
            <Popover2
              content={
                <MenuContent
                  results={results.filter(_resultsInMenu)}
                  selected={selected}
                  onSelect={_onSelect}
                />
              }
              position={Position.BOTTOM_RIGHT}
            >
              <Button
                minimal={true}
                small={true}
                disabled={disabled}
                icon={IconNames.MORE}
                className="select-last-results__more"
              />
            </Popover2>
          )
        }
      </ButtonGroup>
    </div>
  )
}
